import React, { useRef, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, HStack } from '@cad-arc';
import { useGetEventIdAndTabNameFromParams } from '../../hooks/use-get-event-id-and-tab-name-from-params';
import { useEventMetadataStore } from '../../../event-panel/event-panel-form/event-metadata';
import { AttachmentsView, AttachmentsViewHandle } from '../../../attachments/attachments-view';
import { useNavigate } from 'react-router-dom';
import { FR_EVENT_ROOT_PATH, FR_EVENT_ATTACHMENTS_SUB_PATH } from '../../../router/constants';
import {
    MAX_NUM_OF_ATTACHMENTS_PER_EVENT,
    DEFAULT_ATTACHMENT_SORT_OPTION_VALUE,
} from '../../../attachments/constants';
import { AttachmentsHeaderActions } from '../../../attachments/attachments-header-actions';
import { AttachmentSortOptionValue } from '../../../attachments/types';
import { useIsClearedEvent } from '~/event-panel/event-panel-form/use-is-cleared-event';
import { useAttachmentsAbilities } from '~/attachments/hooks/use-attachments-abilities';

export const FrAttachmentsTab = observer(function FrAttachmentsTab(): React.ReactElement | null {
    const { eventId } = useGetEventIdAndTabNameFromParams();
    const navigate = useNavigate();
    const eventMetadataStore = useEventMetadataStore();
    const { canAddAttachments } = useAttachmentsAbilities('CAD_AGENCY_EVENT');
    const eventMetadata = eventMetadataStore.state.metadata;
    const attachmentsViewRef = useRef<AttachmentsViewHandle>(null);
    const [attachmentCount, setAttachmentCount] = useState(0);
    const [sortOption, setSortOption] = useState<AttachmentSortOptionValue>(
        DEFAULT_ATTACHMENT_SORT_OPTION_VALUE
    );
    const isClearedEvent = useIsClearedEvent();
    const handleAttachmentCountChange = useCallback((count: number) => {
        setAttachmentCount(count);
    }, []);

    const handleUploadClick = () => {
        attachmentsViewRef.current?.openFileDialog();
    };

    const isMaxReached = attachmentCount >= MAX_NUM_OF_ATTACHMENTS_PER_EVENT;

    if (!eventId) {
        return null;
    }

    const handleAttachmentClick = (attachmentId: number) => {
        navigate(
            `${FR_EVENT_ROOT_PATH}/${eventId}/${FR_EVENT_ATTACHMENTS_SUB_PATH}/${attachmentId}?sortOption=${sortOption}`
        );
    };

    return (
        <Box h="100%" display="flex" flexDirection="column">
            {canAddAttachments && attachmentCount > 0 && (
                <Box p={3} borderBottom="1px solid" borderColor="gray.200">
                    <HStack justifyContent="flex-end" align="center">
                        <AttachmentsHeaderActions
                            onUploadClick={handleUploadClick}
                            isUploadDisabled={isMaxReached}
                            sortOption={sortOption}
                            onSortChange={setSortOption}
                        />
                    </HStack>
                </Box>
            )}
            <Box flex={1} p={3} overflowY="auto">
                <AttachmentsView
                    ref={attachmentsViewRef}
                    entityId={eventId}
                    entityType="CAD_AGENCY_EVENT"
                    entityNumber={eventMetadata?.agencyEventNumber}
                    onOpenPreview={handleAttachmentClick}
                    onAttachmentCountChange={handleAttachmentCountChange}
                    sortOption={sortOption}
                    isClearedEvent={isClearedEvent}
                />
            </Box>
        </Box>
    );
});
